/**
 * Transformation builder - converts TransformOptions to URL transformation string
 * with deterministic sorting
 */

import type { TransformOptions, LayerOptions } from './types';
import { getDPR } from './device';

interface TransformationPart {
  param: string;
  value: string;
  sortKey: string; // For deterministic sorting
}

function normalizePosition(value: string): string {
  const normalized = value.trim().toLowerCase();
  const positionMap: Record<string, string> = {
    center: 'c',
    'top-left': 'tl',
    'top-right': 'tr',
    'bottom-left': 'bl',
    'bottom-right': 'br',
    top: 't',
    bottom: 'b',
    left: 'l',
    right: 'r',
    p_c: 'c',
    p_tl: 'tl',
    p_tr: 'tr',
    p_bl: 'bl',
    p_br: 'br',
    p_t: 't',
    p_b: 'b',
    p_l: 'l',
    p_r: 'r',
  };
  return positionMap[normalized] || normalized;
}

/** Output format is GIF — omit DPR and quality in delivery URLs (e.g. ARImage / animated GIF). */
export function isGifFormatOutput(transform?: TransformOptions | null): boolean {
  const f = transform?.f;
  return typeof f === 'string' && f.toLowerCase() === 'gif';
}

const VIDEO_FORMATS = new Set(['mp4', 'webm', 'mov', 'avi', 'mkv', 'ogg', 'ogv']);

/** Output format is a video — omit DPR (video resolution is fixed; dpr_ rejected by API). */
export function isVideoFormatOutput(transform?: TransformOptions | null): boolean {
  const f = transform?.f;
  return typeof f === 'string' && VIDEO_FORMATS.has(f.toLowerCase());
}

/**
 * Build transformation string from options
 * Transformations are sorted alphabetically by param prefix
 */
/**
 * `enableDPR` defaults to `false`: reading the device ratio here makes the URL
 * depend on which machine rendered it — 1 on the server, 2 on a retina client —
 * so an SSR app ships one `src` and hydrates a different one, which costs a
 * React attribute mismatch and a second network fetch. A single URL cannot be
 * ratio-correct for every screen anyway; that is what the `1x`/`2x` candidates
 * in `responsiveImageAttributes` are for. Pass `true` to opt back in.
 */
export function buildTransformString(
  transform: TransformOptions,
  defaults?: { f?: string; q?: string | number; [key: string]: any },
  enableDPR: boolean = false
): string {
  const parts: TransformationPart[] = [];
  
  // Helper to add transformation part
  const addPart = (param: string, value: string | number | boolean) => {
    if (value === undefined || value === null) return;
    
    let valueStr: string;
    if (typeof value === 'boolean') {
      valueStr = value ? '' : '';
      if (!value) return; // Skip false booleans
    } else {
      valueStr = String(value);
    }
    
    // Create sort key: param prefix determines order
    // Special handling: dpr_ should come early, but after basic size params
    let sortKey = param;
    if (param.startsWith('dpr_')) {
      sortKey = 'dpr_' + param; // Keep dpr early
    } else if (param.startsWith('a_')) {
      sortKey = 'a_' + param; // Text rotation
    } else if (param.startsWith('ar_')) {
      sortKey = 'ar_' + param;
    } else if (param.startsWith('bg_')) {
      sortKey = 'bg_' + param;
    } else if (param.startsWith('c_')) {
      sortKey = 'c_' + param;
    } else if (param.startsWith('co_')) {
      sortKey = 'co_' + param; // Text color
    } else if (param.startsWith('e_')) {
      sortKey = 'e_' + param;
    } else if (param.startsWith('f_')) {
      sortKey = 'f_' + param;
    } else if (param.startsWith('fl_')) {
      sortKey = 'fl_' + param;
    } else if (param.startsWith('fo_')) {
      sortKey = 'fo_' + param;
    } else if (param.startsWith('flip_')) {
      sortKey = 'flip_' + param;
    } else if (param.startsWith('h_')) {
      sortKey = 'h_' + param;
    } else if (param.startsWith('ip_')) {
      sortKey = 'ip_' + param; // Image layer placement
    } else if (param.startsWith('k_')) {
      sortKey = 'k_' + param;
    } else if (param.startsWith('l_')) {
      sortKey = 'l_' + param;
    } else if (param.startsWith('la_')) {
      sortKey = 'la_' + param;
    } else if (param.startsWith('lar_')) {
      sortKey = 'lar_' + param;
    } else if (param.startsWith('lbg_')) {
      sortKey = 'lbg_' + param;
    } else if (param.startsWith('lc_')) {
      sortKey = 'lc_' + param;
    } else if (param.startsWith('le_')) {
      sortKey = 'le_' + param;
    } else if (param.startsWith('lf_')) {
      sortKey = 'lf_' + param;
    } else if (param.startsWith('lflip_')) {
      sortKey = 'lflip_' + param;
    } else if (param.startsWith('lh_')) {
      sortKey = 'lh_' + param;
    } else if (param.startsWith('lp_')) {
      sortKey = 'lp_' + param;
    } else if (param.startsWith('lq_')) {
      sortKey = 'lq_' + param;
    } else if (param.startsWith('lr_')) {
      sortKey = 'lr_' + param;
    } else if (param.startsWith('ltype_')) {
      sortKey = 'ltype_' + param;
    } else if (param.startsWith('lw_')) {
      sortKey = 'lw_' + param;
    } else if (param.startsWith('lzoom_')) {
      sortKey = 'lzoom_' + param;
    } else if (param.startsWith('lbr_')) {
      sortKey = 'lbr_' + param;
    } else if (param.startsWith('o_')) {
      sortKey = 'o_' + param; // Opacity
    } else if (param.startsWith('p_')) {
      sortKey = 'p_' + param;
    } else if (param.startsWith('ps_')) {
      sortKey = 'ps_' + param;
    } else if (param.startsWith('q_')) {
      sortKey = 'q_' + param;
    } else if (param.startsWith('r_')) {
      sortKey = 'r_' + param;
    } else if (param.startsWith('tp_')) {
      sortKey = 'tp_' + param; // Text placement
    } else if (param.startsWith('w_')) {
      sortKey = 'w_' + param;
    } else if (param.startsWith('x_')) {
      sortKey = 'x_' + param;
    } else if (param.startsWith('y_')) {
      sortKey = 'y_' + param;
    } else if (param.startsWith('z_')) {
      sortKey = 'z_' + param;
    } else if (param.startsWith('br_')) {
      sortKey = 'br_' + param;
    }
    
    parts.push({ param, value: valueStr, sortKey });
  };
  
  // Size & Crop
  if (transform.w !== undefined) addPart('w_', transform.w);
  if (transform.h !== undefined) addPart('h_', transform.h);
  if (transform.fit) {
    const fitMap: Record<string, string> = {
      crop: 'crop',
      fill: 'fill',
      fit: 'fit',
      scale: 'scale'
    };
    addPart('c_', fitMap[transform.fit] || transform.fit);
  }
  if (transform.ar) addPart('ar_', transform.ar);
  if (transform.position) addPart('p_', normalizePosition(transform.position));
  if (transform.position_strategy) {
    const strategy = String(transform.position_strategy).trim().toLowerCase().replace(/^ps_/, '');
    if (strategy === 'entropy' || strategy === 'attention') {
      addPart('ps_', strategy);
    }
  }
  if (transform.focus) addPart('fo_', transform.focus);

  // Video trim (seconds) → so_{n}, eo_{n}
  if (transform.so !== undefined) addPart('so_', transform.so);
  if (transform.eo !== undefined) addPart('eo_', transform.eo);
  
  // Transform
  if (transform.r !== undefined) addPart('r_', transform.r);
  if (transform.z !== undefined) addPart('z_', transform.z);
  if (transform.flip) addPart('flip_', transform.flip);
  if (transform.br !== undefined) addPart('br_', transform.br);
  
  const resolvedFormat = transform.f ?? defaults?.f;
  const isGifOutput =
    typeof resolvedFormat === 'string' && resolvedFormat.toLowerCase() === 'gif';
  const isVideoOutput =
    typeof resolvedFormat === 'string' && VIDEO_FORMATS.has(resolvedFormat.toLowerCase());
  if (resolvedFormat && resolvedFormat !== 'auto') addPart('f_', resolvedFormat);

  // Quality: for GIF, only honor explicit `transform.q` (e.g. 50 → q_50, 90 → q_90); skip defaults.q.
  const quality = isGifOutput ? transform.q : transform.q ?? defaults?.q;
  if (quality !== undefined && quality !== 'auto') addPart('q_', quality);

  // Background
  if (transform.bg) {
    if (transform.bg.startsWith('rgb:')) {
      addPart('bg_rgb:', transform.bg.replace('rgb:', ''));
    } else {
      addPart('bg_', transform.bg);
    }
  }

  // DPR — never for GIF or video output (video resolution is fixed; dpr_ rejected by video API)
  if (!isGifOutput && !isVideoOutput) {
    if (enableDPR && transform.dpr === undefined) {
      const dpr = getDPR();
      if (dpr !== 1) addPart('dpr_', dpr);
    } else if (transform.dpr !== undefined) {
      addPart('dpr_', transform.dpr);
    }
  }
  
  // Effects (e_*)
  if (transform.improve !== undefined) {
    if (typeof transform.improve === 'boolean' && transform.improve) {
      addPart('e_improve', '');
    } else if (typeof transform.improve === 'string') {
      addPart('e_improve', transform.improve);
    } else if (typeof transform.improve === 'object') {
      const { mode, blend } = transform.improve;
      let value = mode || '';
      if (blend !== undefined) value += `:${blend}`;
      addPart('e_improve', value);
    }
  }
  if (transform.unsharpMask !== undefined) addPart('e_unsharp_mask:', transform.unsharpMask);
  if (transform.saturation !== undefined) addPart('e_saturation:', transform.saturation);
  if (transform.contrast !== undefined) addPart('e_contrast:', transform.contrast);
  if (transform.brightness !== undefined) addPart('e_brightness:', transform.brightness);
  if (transform.gamma !== undefined) addPart('e_gamma:', transform.gamma);
  if (transform.sharpen !== undefined) {
    if (typeof transform.sharpen === 'boolean' && transform.sharpen) {
      addPart('e_sharpen', '');
    } else {
      addPart('e_sharpen:', transform.sharpen);
    }
  }
  if (transform.grayscale) addPart('e_grayscale', '');
  if (transform.blackwhite !== undefined) addPart('e_blackwhite:', transform.blackwhite);
  if (transform.hue !== undefined) addPart('e_hue:', transform.hue);
  if (transform.warmth !== undefined) addPart('e_warmth:', transform.warmth);
  if (transform.tint !== undefined) addPart('e_tint:', transform.tint);
  if (transform.normalize) addPart('e_normalize', '');
  if (transform.invert) addPart('e_invert', '');
  if (transform.fade !== undefined) addPart('e_fade:', transform.fade);
  if (transform.vignette !== undefined) addPart('e_vignette:', transform.vignette);
  if (transform.highlights !== undefined) addPart('e_highlights:', transform.highlights);
  if (transform.shadows !== undefined) addPart('e_shadows:', transform.shadows);
  if (transform.exposure !== undefined) addPart('e_exposure:', transform.exposure);
  if (transform.structure !== undefined) addPart('e_structure:', transform.structure);
  if (transform.autoEnhance) addPart('e_auto_enhance', '');
  if (transform.temperature !== undefined) addPart('e_temperature:', transform.temperature);
  if (transform.linear) addPart('e_linear:', transform.linear);
  if (transform.recomb) addPart('e_recomb:', transform.recomb);
  if (transform.threshold !== undefined) addPart('e_threshold:', transform.threshold);
  if (transform.convolve) addPart('e_convolve:', transform.convolve);
  if (transform.unflatten) addPart('e_unflatten', '');
  if (transform.flatten) addPart('e_flatten', '');
  if (transform.median !== undefined) addPart('e_median:', transform.median);
  if (transform.blur !== undefined) addPart('e_blur:', transform.blur);
  if (transform.extend) addPart('e_extend:', transform.extend);
  if (transform.affine) addPart('e_affine:', transform.affine);
  if (transform.extract) addPart('e_extract:', transform.extract);
  if (transform.ensureAlpha) addPart('e_ensure_alpha', '');
  if (transform.removeAlpha) addPart('e_remove_alpha:', transform.removeAlpha);
  
  // Blend mode (for layers)
  if (transform.blend) addPart('e_', transform.blend);
  
  // Raw transformations (for advanced users)
  Object.keys(transform).forEach(key => {
    if (key.startsWith('_') || 
        ['w', 'h', 'fit', 'ar', 'position', 'position_strategy', 'focus', 'so', 'eo', 'r', 'z', 'flip', 'br', 'f', 'q', 'bg', 'dpr',
         'improve', 'unsharpMask', 'saturation', 'contrast', 'brightness', 'gamma', 'sharpen',
         'grayscale', 'blackwhite', 'hue', 'warmth', 'tint', 'normalize', 'invert', 'fade',
         'vignette', 'highlights', 'shadows', 'exposure', 'structure', 'autoEnhance', 'temperature',
         'linear', 'recomb', 'threshold', 'convolve', 'unflatten', 'flatten', 'median', 'blur',
         'extend', 'affine', 'extract', 'ensureAlpha', 'removeAlpha', 'blend', 'layers'].includes(key)) {
      return; // Skip already processed keys
    }
    
    // Allow raw transformation strings
    if (typeof transform[key] === 'string' || typeof transform[key] === 'number' || typeof transform[key] === 'boolean') {
      addPart(key, transform[key] as any);
    }
  });
  
  // Sort base transformation parts deterministically by param prefix
  parts.sort((a, b) => {
    // Extract prefix for comparison
    const prefixA = a.param.split('_')[0] + '_';
    const prefixB = b.param.split('_')[0] + '_';
    
    if (prefixA !== prefixB) {
      return prefixA.localeCompare(prefixB);
    }
    
    // Same prefix, compare full param
    return a.param.localeCompare(b.param);
  });
  
  // Build base transformation string
  const baseTransform = parts.map(p => {
    if (p.value === '') {
      return p.param; // Boolean flags without values
    }
    return `${p.param}${p.value}`;
  }).join(',');
  
  // Build layers separately - each layer must end with fl_layer_apply
  const layerParts: string[] = [];
  if (transform.layers && transform.layers.length > 0) {
    transform.layers.forEach(layer => {
      const layerTransformParts: TransformationPart[] = [];
      
      const addLayerPart = (param: string, value: string | number | boolean) => {
        if (value === undefined || value === null) return;
        
        let valueStr: string;
        if (typeof value === 'boolean') {
          valueStr = value ? '' : '';
          if (!value) return; // Skip false booleans
        } else {
          valueStr = String(value);
        }
        
        layerTransformParts.push({
          param,
          value: valueStr,
          sortKey: param
        });
      };
      
      buildLayerTransform(layer, addLayerPart);
      
      // Sort layer parts with specific order (not alphabetical)
      // Order: l_image/l_text first, then ip_/tp_, then other params, then fl_layer_apply
      const applyFlag = layerTransformParts.find(p => p.param === 'fl_layer_apply');
      const regularParts = layerTransformParts.filter(p => p.param !== 'fl_layer_apply');
      
      // Define order priority for layer parameters
      const getLayerParamOrder = (param: string): number => {
        // l_image or l_text must be first
        if (param.startsWith('l_image:') || param.startsWith('l_text:')) return 0;
        // ip_ (image placement) or tp_ (text placement) comes right after l_image/l_text
        if (param.startsWith('ip_') || param.startsWith('tp_')) return 1;
        // l_type comes after placement
        if (param.startsWith('l_type:')) return 2;
        // lw_, lh_ come after l_type
        if (param.startsWith('lw_')) return 3;
        if (param.startsWith('lh_')) return 4;
        // o_ (opacity) comes after dimensions
        if (param.startsWith('o_')) return 5;
        // Other l* layer params (not lzoom_; see below)
        if (param.startsWith('l') && !param.startsWith('lzoom_')) return 6;
        // co_ (color) for text
        if (param.startsWith('co_')) return 7;
        // a_ (rotation) for text
        if (param.startsWith('a_')) return 8;
        // lr_ (rotation) for image
        if (param.startsWith('lr_')) return 8;
        // lzoom_ after placement/color/rotation, before offsets
        if (param.startsWith('lzoom_')) return 9;
        // x_, y_ offsets
        if (param.startsWith('x_') || param.startsWith('y_')) return 10;
        // le_ (layer enhancements)
        if (param.startsWith('le_')) return 11;
        // e_ (blend mode)
        if (param.startsWith('e_')) return 12;
        // Everything else
        return 100;
      };
      
      regularParts.sort((a, b) => {
        const orderA = getLayerParamOrder(a.param);
        const orderB = getLayerParamOrder(b.param);
        
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        
        // Same order priority, sort alphabetically
        return a.param.localeCompare(b.param);
      });
      
      // Build layer string: regular parts + fl_layer_apply at the end
      const layerString = [
        ...regularParts.map(p => p.value === '' ? p.param : `${p.param}${p.value}`),
        ...(applyFlag ? [applyFlag.param] : [])
      ].join(',');
      
      if (layerString) {
        layerParts.push(layerString);
      }
    });
  }
  
  // Combine: base transformations + layers
  const allParts = baseTransform ? [baseTransform, ...layerParts] : layerParts;
  return allParts.join(',');
}

function buildLayerTransform(layer: LayerOptions, addPart: (param: string, value: string | number | boolean) => void) {
  if (layer.type === 'text') {
    // Build text layer: l_text:fontFamily_fontSize_style:text
    // Example: l_text:arial_24_bold:Demo
    const parts: string[] = [];
    if (layer.fontFamily) parts.push(layer.fontFamily);
    if (layer.fontSize !== undefined) parts.push(String(layer.fontSize));
    if (layer.fontStyle) parts.push(layer.fontStyle);
    if (layer.align) parts.push(layer.align);
    
    const text = layer.text ? encodeURIComponent(layer.text) : '';
    const layerSpec = parts.length > 0 ? `${parts.join('_')}:${text}` : text;
    addPart('l_text:', layerSpec);
    
    // Text layer modifiers (after l_text)
    if (layer.fontColor) {
      // Remove # if present, convert to hex without #
      const color = layer.fontColor.replace('#', '');
      addPart('co_', color);
    }
    if (layer.placement) {
      // Convert placement to short form if needed
      const placement = normalizePlacement(layer.placement);
      if (placement) addPart('tp_', placement);
    }
    if (layer.rotation !== undefined) addPart('a_', layer.rotation);
    if (layer.x !== undefined) addPart('x_', layer.x);
    if (layer.y !== undefined) addPart('y_', layer.y);
  } else if (layer.type === 'image') {
    // Image layer: l_image:imagePath
    // Order: l_image, l_type, lw, lh, ip, o, fl_layer_apply
    // Example: l_image:fav.png,l_type:overlay,lw_200,lh_200,ip_ne,o_100,fl_layer_apply
    if (layer.imagePath) {
      addPart('l_image:', layer.imagePath);
    }
    
    // Image layer modifiers in correct order
    if (layer.layerType) addPart('l_type:', layer.layerType);
    if (layer.width !== undefined) addPart('lw_', layer.width);
    if (layer.height !== undefined) addPart('lh_', layer.height);
    if (layer.placement) {
      // Convert placement to short form if needed
      const placement = normalizePlacement(layer.placement);
      if (placement) addPart('ip_', placement);
    }
    if (layer.opacity !== undefined) addPart('o_', layer.opacity);
    if (layer.rotation !== undefined) addPart('lr_', layer.rotation);
    if (layer.crop) addPart('lc_', layer.crop);
    if (layer.aspectRatio) addPart('lar_', layer.aspectRatio);
    if (layer.quality !== undefined) addPart('lq_', layer.quality);
    if (layer.format) addPart('lf_', layer.format);
    if (layer.background) addPart('lbg_', layer.background);
    if (layer.position) addPart('lp_', layer.position);
    if (layer.borderRadius !== undefined) addPart('lbr_', layer.borderRadius);
    if (layer.flip) addPart('lflip_', layer.flip);
    if (layer.zoom !== undefined) addPart('lzoom_', layer.zoom);
    if (layer.x !== undefined) addPart('x_', layer.x);
    if (layer.y !== undefined) addPart('y_', layer.y);
    if (layer.blendMode) addPart('e_', layer.blendMode);
    
    // Layer enhancements (le_*)
    if (layer.enhance) {
      const enhance = layer.enhance;
      if (enhance.improve !== undefined) {
        if (typeof enhance.improve === 'boolean' && enhance.improve) {
          addPart('le_improve', '');
        } else if (typeof enhance.improve === 'string') {
          addPart('le_improve', enhance.improve);
        }
      }
      if (enhance.sharpen !== undefined) addPart('le_sharpen:', enhance.sharpen);
      if (enhance.brightness !== undefined) addPart('le_brightness:', enhance.brightness);
      if (enhance.saturation !== undefined) addPart('le_saturation:', enhance.saturation);
      if (enhance.contrast !== undefined) addPart('le_contrast:', enhance.contrast);
      if (enhance.hue !== undefined) addPart('le_hue:', enhance.hue);
      if (enhance.warmth !== undefined) addPart('le_warmth:', enhance.warmth);
      if (enhance.tint !== undefined) addPart('le_tint:', enhance.tint);
      if (enhance.normalize) addPart('le_normalize', '');
      if (enhance.invert) addPart('le_invert', '');
      if (enhance.fade !== undefined) addPart('le_fade:', enhance.fade);
      if (enhance.vignette !== undefined) addPart('le_vignette:', enhance.vignette);
      if (enhance.highlights !== undefined) addPart('le_highlights:', enhance.highlights);
      if (enhance.shadows !== undefined) addPart('le_shadows:', enhance.shadows);
      if (enhance.exposure !== undefined) addPart('le_exposure:', enhance.exposure);
      if (enhance.structure !== undefined) addPart('le_structure:', enhance.structure);
      if (enhance.autoEnhance) addPart('le_auto_enhance', '');
      if (enhance.temperature !== undefined) addPart('le_temperature:', enhance.temperature);
      if (enhance.blackwhite !== undefined) addPart('le_blackwhite:', enhance.blackwhite);
      if (enhance.gamma !== undefined) addPart('le_gamma:', enhance.gamma);
      if (enhance.unsharpMask !== undefined) addPart('le_unsharp_mask:', enhance.unsharpMask);
      if (enhance.grayscale) addPart('le_grayscale', '');
    }
  }
  
  // Layer apply flag (always at the end)
  addPart('fl_layer_apply', '');
}

/**
 * Normalize placement values to short form
 * north -> n, north-east -> ne, etc.
 */
function normalizePlacement(placement: string): string {
  const normalized = placement.trim().toLowerCase().replace(/\s+/g, ' ');
  const mapping: Record<string, string> = {
    'north': 'n',
    'south': 's',
    'east': 'e',
    'west': 'w',
    'center': 'c',
    'north-west': 'nw',
    'north west': 'nw',
    'north-east': 'ne',
    'north east': 'ne',
    'south-west': 'sw',
    'south west': 'sw',
    'south-east': 'se',
    'south east': 'se',
    'none': 'none'
  };
  return mapping[normalized] || normalized;
}

